import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Play, Phone, Search, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import RecordingPlayerDialog from '@/components/voice/RecordingPlayerDialog';
import { voiceAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const ALL = '__all__';

export default function CallHistoryPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canPlay, setCanPlay] = useState(false);
  const [agents, setAgents] = useState([]);
  const [playerSid, setPlayerSid] = useState(null);

  // Filters
  const [q, setQ] = useState('');
  const [agent, setAgent] = useState(ALL);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [recordingsOnly, setRecordingsOnly] = useState(false);

  useEffect(() => {
    voiceAPI.assignableAgents()
      .then((r) => setAgents(r.data.items || []))
      .catch(() => {});
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (agent && agent !== ALL) params.agent = agent;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (recordingsOnly) params.recordings_only = true;
      const r = await voiceAPI.calls(params);
      setCalls(r.data.items || []);
      setCanPlay(!!r.data.can_play_recording);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Search failed'));
    } finally {
      setLoading(false);
    }
  }, [q, agent, fromDate, toDate, recordingsOnly]);

  // Load on mount and whenever the agent / recordings / date filters change.
  // The free-text number search is applied on Enter / Search button.
  useEffect(() => { search(); }, [agent, fromDate, toDate, recordingsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setQ(''); setAgent(ALL); setFromDate(''); setToDate(''); setRecordingsOnly(false);
  };

  const agentName = (uid) => agents.find((a) => a.user_id === uid)?.name;

  return (
    <div data-testid="call-history-page">
      <PageHeader
        title="Call History"
        description="Audit every call across the organization. Search by agent, number, or date."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Call History', href: '/call-history' },
        ]}
      />
      <div className="page-content space-y-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search number</Label>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && search()}
                  placeholder="+1863…"
                  className="h-9 text-sm"
                  data-testid="call-history-search-number"
                />
              </div>
              <div className="min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Agent</Label>
                <Select value={agent} onValueChange={setAgent}>
                  <SelectTrigger className="h-9 text-sm" data-testid="call-history-agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All agents</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 text-sm" data-testid="call-history-from" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 text-sm" data-testid="call-history-to" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 h-9">
                <Checkbox checked={recordingsOnly} onCheckedChange={(v) => setRecordingsOnly(!!v)} data-testid="call-history-recordings-only" />
                Recordings only
              </label>
              <Button size="sm" onClick={search} data-testid="call-history-search-btn">
                <Search size={14} className="mr-1" /> Search
              </Button>
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="call-history-clear">
                <X size={14} className="mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : calls.length === 0 ? (
              <p className="text-sm text-slate-500 py-10 text-center flex flex-col items-center gap-2">
                <Phone size={24} className="text-slate-300" />
                No calls match these filters.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="text-xs text-slate-500 mb-2" data-testid="call-history-count">{calls.length} call(s)</div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Direction</th>
                      <th className="px-3 py-2 text-left">Agent</th>
                      <th className="px-3 py-2 text-left">From</th>
                      <th className="px-3 py-2 text-left">To</th>
                      <th className="px-3 py-2 text-left">Disposition</th>
                      <th className="px-3 py-2 text-left">Duration</th>
                      <th className="px-3 py-2 text-left">When</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {calls.map((c) => (
                      <tr key={c.call_sid} data-testid={`call-history-row-${c.call_sid}`}>
                        <td className="px-3 py-2">
                          <Badge className={c.direction === 'inbound' ? 'bg-sky-100 text-sky-800' : 'bg-indigo-100 text-indigo-800'}>{c.direction}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">
                          {c.owner_name || agentName(c.owner_user_id) || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {c.from_name && <div className="text-xs font-medium text-slate-800">{c.from_name}</div>}
                          <div className="font-mono text-xs text-slate-500">{c.from_number || '—'}</div>
                        </td>
                        <td className="px-3 py-2">
                          {c.to_name && <div className="text-xs font-medium text-slate-800">{c.to_name}</div>}
                          <div className="font-mono text-xs text-slate-500">{c.to_number || (c.forwarded_to ? `→ ${c.forwarded_to}` : '—')}</div>
                        </td>
                        <td className="px-3 py-2"><DispositionBadge disposition={c.disposition} rawStatus={c.status} /></td>
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">{c.duration_display || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {c.started_at ? new Date(c.started_at).toLocaleString() : (c.created_at ? new Date(c.created_at).toLocaleString() : '—')}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {c.recording_sid && canPlay && (
                            <Button size="sm" variant="outline" onClick={() => setPlayerSid(c.recording_sid)} data-testid={`call-history-play-${c.recording_sid}`}>
                              <Play size={13} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <RecordingPlayerDialog
        open={!!playerSid}
        recordingSid={playerSid}
        title="Call recording"
        onClose={() => setPlayerSid(null)}
      />
    </div>
  );
}

const DISPOSITION_META = {
  answered: { label: 'Answered', cls: 'bg-emerald-100 text-emerald-800' },
  no_answer: { label: 'No answer', cls: 'bg-rose-100 text-rose-800' },
  voicemail: { label: 'Voicemail', cls: 'bg-amber-100 text-amber-800' },
  forwarded: { label: 'Forwarded', cls: 'bg-violet-100 text-violet-800' },
  in_progress: { label: 'In progress', cls: 'bg-slate-100 text-slate-600' },
};

function DispositionBadge({ disposition, rawStatus }) {
  const meta = DISPOSITION_META[disposition] || { label: rawStatus || disposition || 'Unknown', cls: 'bg-slate-100 text-slate-600' };
  return <Badge className={`${meta.cls} capitalize`}>{meta.label}</Badge>;
}
